/** Timezone detection, cache, and display offset. */

import { logger } from '@/lib/logger'

export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function getCurrentDateTime(): {
  date: string
  time: string
  timezone: string
} {
  const now = new Date()
  const timezone = getUserTimezone()

  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')

  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}`,
    timezone,
  }
}

/** Detect timezone: Intl → localStorage cache → alternative Intl → offset → UTC. */
export function detectTimezone(): string {
  let timezone: string | undefined

  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (timezone) {
      if (typeof window !== 'undefined') {
        localStorage.setItem('dday-user-timezone', timezone)
      }
      return timezone
    }
  } catch (e) {
    logger.warn('Primary timezone detection failed:', e)
  }

  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem('dday-user-timezone')
    if (cached) return cached
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'long',
    }).formatToParts(new Date())
    timezone = parts.find((p) => p.type === 'timeZoneName')?.value
    if (timezone) return timezone
  } catch (e) {
    logger.warn('Alternative timezone detection failed:', e)
  }

  try {
    const offset = -new Date().getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const mins = Math.abs(offset) % 60
    const sign = offset >= 0 ? '+' : '-'
    return `Etc/GMT${sign}${hours}${mins ? `:${mins}` : ''}`
  } catch (e) {
    logger.warn('Offset-based timezone calculation failed:', e)
  }

  return 'UTC'
}

export function getCachedTimezone(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem('dday-user-timezone')
}

export function clearCachedTimezone(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem('dday-user-timezone')
}

/** Display offset (e.g. UTC-5). Zero offset normalized to UTC — Intl can return GMT/GMT+0 by environment. */
export function getTimezoneOffset(timezone?: string): string {
  try {
    const tz = timezone || detectTimezone()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(new Date())
    const value = parts.find((p) => p.type === 'timeZoneName')?.value || 'UTC'
    if (/^(GMT([+-]0)?|UTC)$/.test(value)) return 'UTC'
    return value
  } catch {
    return 'UTC'
  }
}

export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true
  } catch {
    return false
  }
}
